import { ChakraProvider, Spinner } from '@chakra-ui/react';
import { CustomTheme } from './theme/theme';
import { Routes, Route, HashRouter, Navigate, useLocation } from 'react-router-dom';
import React, { Fragment, Suspense, useEffect, useState } from 'react';
import Overlay from './components/common/Overlay';
import { Helmet } from 'react-helmet';
import RegionNotAllowed from './components/RegionNotAllowed';
import Support from './pages/Support';
import axios from 'axios';
import { ShopListItem } from './interfaces/components';
import { useShopStore } from './store/useShopStore';
import SiteBlocked from './components/SiteBlocked';
// import axios from 'axios';

const Shops = React.lazy(() => import('./pages/Shops'));
const Viewer = React.lazy(() => import('./pages/Viewer'));
const Home = React.lazy(() => import('./pages/Home'));
const MetaTagsComponent = React.lazy(() => import('./components/MetaTagsComponent'));

// Component that handles location-dependent logic
function AppContent({ setShowSite }: { setShowSite: (show: boolean) => void }) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const filter = queryParams.get('filter') || '';

  let fetchStoresUrl = `${process.env.REACT_APP_API_URL}/experiences/published?limit=200`;
  if (process.env.REACT_APP_ENVIRONMENT === 'staging' || process.env.REACT_APP_ENVIRONMENT === 'development') {
    if (filter && filter !== '') {
      fetchStoresUrl = `${process.env.REACT_APP_API_URL}/experiences/all?limit=500&status=${filter}`;
    } else {
      fetchStoresUrl = `${process.env.REACT_APP_API_URL}/experiences/all?limit=500`;
    }
  }
  const { setShops } = useShopStore();

  const fetchAllStores = () => {
    axios
      .get(fetchStoresUrl)
      .then((response) => {
        const fetchedStored: ShopListItem[] = response.data.data;
        // const filteredStores = fetchedStored.filter((store) => store?.createdAt && store?.createdAt !== null);
        // Sort by date first, then arrange to avoid adjacent same themes
        const datesorted = fetchedStored.sort(
          (a, b) => new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime(),
        );

        // Rearrange to avoid adjacent same themes
        const sortedStores: ShopListItem[] = [];
        const remaining = [...datesorted];

        while (remaining.length > 0) {
          let nextStore: ShopListItem | null = null;
          const lastTheme = sortedStores.length > 0 ? sortedStores[sortedStores.length - 1].theme : null;

          // Try to find a store with a different theme
          const differentThemeIndex = remaining.findIndex((store) => store?.theme !== lastTheme);

          if (differentThemeIndex !== -1) {
            nextStore = remaining[differentThemeIndex];
            remaining.splice(differentThemeIndex, 1);
          } else if (remaining.length > 0) {
            // If no different theme available, take the first one (maintains date order as fallback)
            nextStore = remaining.shift() || null;
          }

          if (nextStore) {
            sortedStores.push(nextStore);
          }
        }

        setShops(sortedStores);
      })
      .catch((error) => {
        console.error('Error fetching stores:', error);
      });
  };

  useEffect(() => {
    fetchAllStores();
  }, [filter]); // Re-fetch when filter changes

  return (
    <Overlay setShowSite={setShowSite}>
      <Suspense
        fallback={
          <Spinner color="black" size="lg" position="fixed" left={0} right={0} top={0} bottom={0} margin="auto" />
        }
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/feed" element={<Shops />} />
          <Route path="/viewer/:stringUserName/:stringStoreTitle" element={<Viewer />} />
          <Route path="/viewer/:stringUserName/:stringStoreTitle/:slotId" element={<Viewer />} />
          <Route path="/feed" element={<Shops />} />
          <Route path="/support" element={<Support />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </Overlay>
  );
}

function App() {
  const [showSite, setShowSite] = useState<boolean | null>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined = undefined;

    const fetchRegion = async (regionAllowed: boolean) => {
      if (!regionAllowed) {
        setShowSite(false);

        // Clear local storage
        localStorage.clear();

        // const hasCookies = document.cookie.length > 0;

        // Clear all cookies
        document.cookie.split(';').forEach((cookie) => {
          const [name] = cookie.split('=');
          // Domain is required to ensure cookies are cleared from the correct domain scope
          // Without domain, cookies may only be cleared from the current subdomain
          document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=walmartrealm.com`;
        });
        // Clear third-party cookies by specifying their domains
        document.cookie = `_ttp=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=.tiktok.com`;
        document.cookie = `wm_phase_four_seen=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=walmartrealm.com`;
        document.cookie = `wm_phase_four_seen=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;

        // hasCookies && window.location.reload();

        // redirect to walmart
        setTimeout(() => {
          window.open('https://www.walmart.com/', '_self');
        }, 5000);
      } else {
        setShowSite(true);
      }
    };

    // const regionAllowed = !['', ''].includes(window.region);
    const regionAllowed = !['MX', 'CA'].includes(window.region);
    console.log('window.region', window.region);
    console.log('regionAllowed', regionAllowed);
    const hasCookies = document.cookie.length > 0;

    if (hasCookies && !regionAllowed) {
      fetchRegion(regionAllowed);
    } else {
      timeoutId = setTimeout(() => {
        fetchRegion(regionAllowed);
      }, 900);
    }

    return () => clearTimeout(timeoutId); // Cleanup timeout
  }, []);

  if (showSite === null) {
    return (
      <ChakraProvider theme={CustomTheme}>
        <Spinner color="black" size="lg" position="fixed" left={0} right={0} top={0} bottom={0} margin="auto" />
      </ChakraProvider>
    );
  }

  if (!showSite) {
    console.log('Site Blocked, Not Available', window.location.href);
    return (
      <ChakraProvider theme={CustomTheme}>
        <HashRouter>
          <RegionNotAllowed />
        </HashRouter>
      </ChakraProvider>
    );
  }

  if (window.location.href.includes('walmartrealm.com')) {
    console.log('Site Blocked, Coming Soon', window.location.href);
    return (
      <ChakraProvider theme={CustomTheme}>
        <HashRouter>
          <Routes>
            <Route path="/" element={<SiteBlocked active={true} />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </HashRouter>
      </ChakraProvider>
    );
  }

  const isProd = process.env.REACT_APP_DOMAIN === 'https://walmartrealm.com';

  return (
    <ChakraProvider theme={CustomTheme}>
      {showSite && (
        <Fragment>
          {isProd && <MetaTagsComponent />}
          {!isProd && (
            <Helmet>
              <meta name="robots" content="noindex, nofollow" />
            </Helmet>
          )}
          <HashRouter>
            <AppContent setShowSite={setShowSite} />
          </HashRouter>
        </Fragment>
      )}
      {/* <RegionNotAllowed active={!showSite} /> */}
    </ChakraProvider>
  );
}

export default App;
